
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useBookingRealtimeSync } from '@/hooks/useBookingRealtimeSync';

interface Appointment {
  id: string;
  start_time: string;
  end_time: string;
  status: string;
  format: string;
  payment_status: string;
  client_notes?: string;
  practitioner_notes?: string;
  refund_status?: string;
  refund_reason?: string;
  client_id: string;
  service: {
    service_name: string;
  };
  clients: {
    first_name: string | null;
    last_name: string | null;
    
  };
}

export const usePractitionerAppointmentHistory = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const [selectedNotes, setSelectedNotes] = useState<string | null>(null);

  const fetchAppointments = useCallback(async () => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      console.log('Fetching practitioner appointment history via RPC...');

      const { data, error } = await supabase
        .rpc('get_practitioner_bookings_with_client');

      if (error) throw error;

      // 历史：非 Scheduled 并且排除所有 Cancelled 状态的预约
      const filtered = (data || []).filter((item: any) => {
        // Exclude all cancelled appointments (including 'Cancelled by Client')
        if (item.status && item.status.includes('Cancelled')) {
          return false;
        }
        // Exclude scheduled appointments from history
        if (item.status === 'Scheduled') {
          return false;
        }
        return true;
      });

      // 按开始时间降序
      filtered.sort((a: any, b: any) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime());

      console.log(`Fetched ${filtered.length} practitioner appointment history (RPC):`, filtered);

      const formattedData: Appointment[] = filtered.map((item: any) => ({
        id: item.id,
        start_time: item.start_time,
        end_time: item.end_time,
        status: item.status,
        format: item.format,
        payment_status: item.payment_status || 'Unknown',
        client_notes: item.client_notes,
        practitioner_notes: item.practitioner_notes,
        refund_status: item.refund_status,
        refund_reason: item.refund_reason,
        client_id: item.client_id,
        service: {
          service_name: item.service_name || 'Unknown Service'
        },
        clients: {
          first_name: item.client_first_name ?? null,
          last_name: item.client_last_name ?? null,
          email: item.client_email ?? null
        }
      }));
      
      setAppointments(formattedData);
    } catch (error) {
      console.error('Error fetching practitioner appointment history (RPC):', error);
      toast({
        title: "Failed to load history",
        description: "There was an error loading your appointment history",
        variant: "destructive",
        duration: 8000,
      });
      setAppointments([]);
    } finally {
      setIsLoading(false);
    }
  }, [user, toast]);

  // Refresh data when the component mounts, when user changes, or when refreshTrigger changes
  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments, refreshTrigger]);

  // Function to manually trigger a refresh
  const refreshAppointments = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Add realtime sync for appointment updates
  useBookingRealtimeSync({
    userId: user?.id,
    type: 'practitioner',
    onBookingUpdate: (updatedBooking) => {
      console.log('Booking update received in practitioner history:', updatedBooking);
      setAppointments(appointments => {
        const existingAppointmentIndex = appointments.findIndex(appt => appt.id === updatedBooking.id);
        
        if (existingAppointmentIndex >= 0) {
          const updatedAppointments = [...appointments];
          updatedAppointments[existingAppointmentIndex] = {
            ...updatedAppointments[existingAppointmentIndex],
            ...updatedBooking,
            payment_status: updatedBooking.payment_status || updatedAppointments[existingAppointmentIndex].payment_status
          };
          return updatedAppointments;
        } else {
          return appointments;
        }
      });
    },
    onBookingInsert: (newBooking) => {
      console.log('New booking received in practitioner history:', newBooking);
      fetchAppointments();
    },
    onBookingDelete: (deletedBooking) => {
      console.log('Booking deletion received in practitioner history:', deletedBooking);
      setAppointments(appointments => appointments.filter(appt => appt.id !== deletedBooking.id));
    }
  });

  return {
    appointments,
    isLoading,
    selectedNotes,
    setSelectedNotes,
    fetchAppointments,
    refreshAppointments
  };
};
